import { apiClient } from "@/lib/api";
import type {
  WorkOrder,
  WorkOrderWriteBody,
  WorkOrderTransitionStatus,
  ServiceSchedule,
  ServiceScheduleWriteBody,
  ServiceSummary,
  ServiceReminder,
  ReminderRunStats,
  ReferenceIndex,
  ReferenceManufacturer,
  ApplyPlanResult,
} from "@/types/service";

// --- Work Orders ---

export const fetchWorkOrders = async (filters?: {
  q?: string;
  boat_id?: string;
  status?: string;
  priority?: string;
  open_only?: boolean;
  due_before?: string;
  technician?: string;
  // Bounds the work order's calendar date (scheduled_date, falling back to
  // due_date) — RFC3339 instants.
  from?: string;
  to?: string;
}): Promise<WorkOrder[]> => {
  return apiClient<WorkOrder[]>("/service/work-orders", { params: filters });
};

export const fetchWorkOrderTechnicians = async (): Promise<string[]> => {
  return apiClient<string[]>("/service/work-orders/technicians");
};

// Roster-based options for the assignment dropdown: active Service-department
// employees (the staff managed in account admin settings).
export const fetchAssignableTechnicians = async (): Promise<string[]> => {
  return apiClient<string[]>("/service/work-orders/assignable-technicians");
};

export const fetchWorkOrderById = async (id: string): Promise<WorkOrder> => {
  return apiClient<WorkOrder>(`/service/work-orders/${id}`);
};

export const createWorkOrder = async (body: WorkOrderWriteBody): Promise<WorkOrder> => {
  return apiClient<WorkOrder>("/service/work-orders", { method: "POST", body });
};

export const updateWorkOrder = async (
  id: string,
  body: Partial<WorkOrderWriteBody>
): Promise<WorkOrder> => {
  return apiClient<WorkOrder>(`/service/work-orders/${id}`, { method: "PUT", body });
};

export const deleteWorkOrder = async (id: string): Promise<void> => {
  return apiClient<void>(`/service/work-orders/${id}`, { method: "DELETE" });
};

export const changeWorkOrderStatus = async (
  id: string,
  status: WorkOrderTransitionStatus,
  engineHoursAtService?: number
): Promise<WorkOrder> => {
  return apiClient<WorkOrder>(`/service/work-orders/${id}/status`, {
    method: "PATCH",
    body: engineHoursAtService !== undefined ? { status, engine_hours_at_service: engineHoursAtService } : { status },
  });
};

// --- Summary ---

export const fetchServiceSummary = async (): Promise<ServiceSummary> => {
  return apiClient<ServiceSummary>("/service/summary");
};

// --- Schedules ---

export const fetchSchedules = async (filters?: { q?: string; boat_id?: string }) => {
  return apiClient<ServiceSchedule[]>("/service/schedules", { params: filters });
};

export const createSchedule = async (body: ServiceScheduleWriteBody): Promise<ServiceSchedule> => {
  return apiClient<ServiceSchedule>("/service/schedules", { method: "POST", body });
};

export const updateSchedule = async (
  id: string,
  body: Partial<ServiceScheduleWriteBody>
): Promise<ServiceSchedule> => {
  return apiClient<ServiceSchedule>(`/service/schedules/${id}`, { method: "PUT", body });
};

export const deleteSchedule = async (id: string): Promise<void> => {
  return apiClient<void>(`/service/schedules/${id}`, { method: "DELETE" });
};

// --- Manufacturer reference data + plans ---

export const fetchReferenceIndex = async (): Promise<ReferenceIndex> => {
  return apiClient<ReferenceIndex>("/service/reference");
};

export const fetchReferenceManufacturer = async (key: string): Promise<ReferenceManufacturer> => {
  return apiClient<ReferenceManufacturer>(`/service/reference/${key}`);
};

export const fetchPlanSuggestion = async (boatId: string): Promise<{ suggested_manufacturer: string }> => {
  return apiClient<{ suggested_manufacturer: string }>(`/service/plans/suggest/${boatId}`);
};

export const applyPlan = async (body: {
  boat_id: string;
  manufacturer_key?: string;
  item_keys?: string[];
  include_boat_level?: boolean;
}): Promise<ApplyPlanResult> => {
  return apiClient<ApplyPlanResult>("/service/plans/apply", { method: "POST", body });
};

// --- Reminders ---

export const fetchReminders = async (limit = 100): Promise<ServiceReminder[]> => {
  return apiClient<ServiceReminder[]>("/service/reminders", { params: { limit } });
};

export const runReminderJob = async (): Promise<ReminderRunStats> => {
  return apiClient<ReminderRunStats>("/service/reminders/run", { method: "POST" });
};
